import React, { useCallback, useEffect, useState, createContext, useContext } from 'react';
type Language = 'en' | 'kn';
interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
  hasSelectedLanguage: boolean;
  setHasSelectedLanguage: (val: boolean) => void;
}
const translations: Record<string, Record<Language, string>> = {
  // General
  'app.name': {
    en: 'Theo EduMarket Rwanda',
    kn: 'Theo EduMarket Rwanda'
  },
  'app.tagline': {
    en: 'Your Digital Educational Marketplace',
    kn: "Isoko ryawe ry'ikoranabuhanga ry'uburezi"
  },
  'app.description': {
    en: 'Buy and download past papers, books, and educational materials for Rwandan students.',
    kn: "Gura kandi ukurure ibizamini byashize, ibitabo, n'ibikoresho by'uburezi ku banyeshuri b'u Rwanda."
  },
  // Language Select
  'lang.choose': {
    en: 'Choose Your Language',
    kn: 'Hitamo Ururimi Rwawe'
  },
  'lang.welcome': {
    en: 'Welcome to Theo EduMarket Rwanda',
    kn: 'Murakaza neza kuri Theo EduMarket Rwanda'
  },
  'lang.english': {
    en: 'English',
    kn: 'Icyongereza'
  },
  'lang.kinyarwanda': {
    en: 'Kinyarwanda',
    kn: 'Ikinyarwanda'
  },
  // Navigation
  'nav.home': {
    en: 'Home',
    kn: 'Ahabanza'
  },
  'nav.marketplace': {
    en: 'Marketplace',
    kn: 'Isoko'
  },
  'nav.chat': {
    en: 'Chat with Admin',
    kn: 'Ganira na Admin'
  },
  'nav.admin': {
    en: 'Admin Panel',
    kn: 'Ikicaro cya Admin'
  },
  'nav.login': {
    en: 'Login',
    kn: 'Injira'
  },
  'nav.signup': {
    en: 'Sign Up',
    kn: 'Iyandikishe'
  },
  'nav.logout': {
    en: 'Logout',
    kn: 'Sohoka'
  },
  'nav.profile': {
    en: 'Profile',
    kn: 'Umwirondoro'
  },
  // Landing
  'landing.hero.title': {
    en: 'Access Quality Education Materials',
    kn: "Bona Ibikoresho by'Uburezi Byiza"
  },
  'landing.hero.subtitle': {
    en: 'Download past papers, textbooks, and study guides for all levels of Rwandan education.',
    kn: "Kurura ibizamini byashize, ibitabo, n'amabwiriza y'kwiga ku nzego zose z'uburezi mu Rwanda."
  },
  'landing.hero.browse': {
    en: 'Browse Marketplace',
    kn: 'Reba Isoko'
  },
  'landing.hero.signup': {
    en: 'Get Started',
    kn: 'Tangira'
  },
  'landing.about.title': {
    en: 'About Theo EduMarket',
    kn: 'Ibyerekeye Theo EduMarket'
  },
  'landing.about.text': {
    en: 'Theo EduMarket Rwanda is a digital platform connecting students with quality educational resources. We provide past examination papers, textbooks, literature, and study materials for primary, secondary, and advanced levels.',
    kn: "Theo EduMarket Rwanda ni urubuga rw'ikoranabuhanga ruhuza abanyeshuri n'ibikoresho by'uburezi byiza. Dutanga ibizamini byashize, ibitabo, ubuvanganzo, n'ibikoresho byo kwiga ku mashuri abanza, ayisumbuye, n'amashuri makuru."
  },
  'landing.how.title': {
    en: 'How It Works',
    kn: 'Uko Bikora'
  },
  'landing.how.step1.title': {
    en: 'Browse',
    kn: 'Shakisha'
  },
  'landing.how.step1.text': {
    en: 'Explore our collection of educational materials',
    kn: "Reba ububiko bwacu bw'ibikoresho by'uburezi"
  },
  'landing.how.step2.title': {
    en: 'Pay',
    kn: 'Ishyura'
  },
  'landing.how.step2.text': {
    en: 'Pay securely via MTN Mobile Money',
    kn: 'Ishyura neza ukoresheje MTN Mobile Money'
  },
  'landing.how.step3.title': {
    en: 'Download',
    kn: 'Kurura'
  },
  'landing.how.step3.text': {
    en: 'Get instant access after admin verification',
    kn: 'Bona uburenganzira bwihuse nyuma yo kwemezwa na admin'
  },
  'landing.featured': {
    en: 'Featured Materials',
    kn: 'Ibikoresho Byihariye'
  },
  'landing.contact.title': {
    en: 'Contact Us',
    kn: 'Twandikire'
  },
  // Auth
  'auth.login.title': {
    en: 'Welcome Back',
    kn: 'Murakaza neza'
  },
  'auth.signup.title': {
    en: 'Create Account',
    kn: 'Fungura Konti'
  },
  'auth.phone': {
    en: 'Phone Number',
    kn: 'Nimero ya Telefoni'
  },
  'auth.phone.placeholder': {
    en: 'Enter 10-digit number',
    kn: "Andika nimero y'imibare 10"
  },
  'auth.fullname': {
    en: 'Full Name',
    kn: 'Amazina Yombi'
  },
  'auth.fullname.placeholder': {
    en: 'Enter your full name',
    kn: 'Andika amazina yawe yombi'
  },
  'auth.password': {
    en: 'Password',
    kn: "Ijambo ry'ibanga"
  },
  'auth.password.confirm': {
    en: 'Confirm Password',
    kn: "Emeza Ijambo ry'ibanga"
  },
  'auth.remember': {
    en: 'Remember me',
    kn: 'Unyibuke'
  },
  'auth.login.btn': {
    en: 'Login',
    kn: 'Injira'
  },
  'auth.signup.btn': {
    en: 'Sign Up',
    kn: 'Iyandikishe'
  },
  'auth.switch.login': {
    en: 'Already have an account? Login',
    kn: 'Usanzwe ufite konti? Injira'
  },
  'auth.switch.signup': {
    en: "Don't have an account? Sign Up",
    kn: 'Nta konti ufite? Iyandikishe'
  },
  'auth.country': {
    en: 'Country Code',
    kn: "Kode y'Igihugu"
  },
  'auth.error.phone': {
    en: 'Phone number must be exactly 10 digits',
    kn: 'Nimero ya telefoni igomba kuba imibare 10 gusa'
  },
  'auth.error.password.match': {
    en: 'Passwords do not match',
    kn: "Amagambo y'ibanga ntabwo ahura"
  },
  'auth.error.required': {
    en: 'All fields are required',
    kn: 'Ahantu hose ni ngombwa'
  },
  'auth.error.invalid': {
    en: 'Invalid phone number or password',
    kn: "Nimero ya telefoni cyangwa ijambo ry'ibanga ntabwo ari byo"
  },
  'auth.success.signup': {
    en: 'Account created successfully!',
    kn: 'Konti yafunguwe neza!'
  },
  'auth.success.login': {
    en: 'Logged in successfully!',
    kn: 'Winjiye neza!'
  },
  // Marketplace
  'market.title': {
    en: 'Marketplace',
    kn: 'Isoko'
  },
  'market.search': {
    en: 'Search materials...',
    kn: 'Shakisha ibikoresho...'
  },
  'market.filter.all': {
    en: 'All',
    kn: 'Byose'
  },
  'market.filter.books': {
    en: 'Books',
    kn: 'Ibitabo'
  },
  'market.filter.pastpapers': {
    en: 'Past Papers',
    kn: 'Ibizamini Byashize'
  },
  'market.filter.other': {
    en: 'Other',
    kn: 'Ibindi'
  },
  'market.buy': {
    en: 'Buy & Download',
    kn: 'Gura & Kurura'
  },
  'market.purchased': {
    en: 'Purchased ✓',
    kn: 'Byaguzwe ✓'
  },
  'market.download': {
    en: 'Download',
    kn: 'Kurura'
  },
  'market.price': {
    en: 'Price',
    kn: 'Igiciro'
  },
  'market.empty': {
    en: 'No materials found',
    kn: 'Nta bikoresho byabonetse'
  },
  'market.frw': {
    en: 'FRW',
    kn: 'FRW'
  },
  // Payment
  'payment.title': {
    en: 'Payment',
    kn: 'Kwishyura'
  },
  'payment.method': {
    en: 'Pay with MTN Mobile Money',
    kn: 'Ishyura na MTN Mobile Money'
  },
  'payment.instructions': {
    en: 'Send the amount to the following MTN number:',
    kn: 'Ohereza amafaranga kuri nimero ya MTN ikurikira:'
  },
  'payment.amount': {
    en: 'Amount',
    kn: 'Amafaranga'
  },
  'payment.mtn.number': {
    en: 'MTN Number',
    kn: 'Nimero ya MTN'
  },
  'payment.mtn.name': {
    en: 'Account Name',
    kn: "Izina ry'Konti"
  },
  'payment.verify.title': {
    en: 'Verify Your Payment',
    kn: 'Emeza Kwishyura Kwawe'
  },
  'payment.verify.phone': {
    en: 'Your MTN number used to pay',
    kn: 'Nimero yawe ya MTN wakoresheje wishyura'
  },
  'payment.verify.name': {
    en: 'Name on your MTN account',
    kn: 'Izina kuri konti yawe ya MTN'
  },
  'payment.submit': {
    en: 'Submit for Verification',
    kn: 'Ohereza Kwemezwa'
  },
  'payment.pending': {
    en: 'Payment Pending Verification',
    kn: 'Kwishyura Gutegereje Kwemezwa'
  },
  'payment.pending.msg': {
    en: 'Your payment is being reviewed by the admin. You can wait or leave and come back later.',
    kn: 'Kwishyura kwawe kurimo gusuzumwa na admin. Ushobora gutegereza cyangwa ugasubira nyuma.'
  },
  'payment.verified': {
    en: 'Payment Verified!',
    kn: 'Kwishyura Kwemejwe!'
  },
  'payment.denied': {
    en: 'Payment Denied',
    kn: 'Kwishyura Kwanzwe'
  },
  'payment.denied.msg': {
    en: 'You are not registered by admin. Make sure you paid the correct amount.',
    kn: 'Ntabwo wanditswe na admin. Menya neza ko wishyuye amafaranga akwiye.'
  },
  // Admin
  'admin.login.title': {
    en: 'Admin Login',
    kn: 'Kwinjira kwa Admin'
  },
  'admin.email': {
    en: 'Email',
    kn: 'Imeyili'
  },
  'admin.password': {
    en: 'Password',
    kn: "Ijambo ry'ibanga"
  },
  'admin.login.btn': {
    en: 'Login as Admin',
    kn: 'Injira nka Admin'
  },
  'admin.error.credentials': {
    en: 'Invalid email or password',
    kn: "Imeyili cyangwa ijambo ry'ibanga ntabwo ari byo"
  },
  'admin.error.locked': {
    en: 'Account locked. Try again in 5 minutes.',
    kn: 'Konti ifunze. Ongera ugerageze mu minota 5.'
  },
  'admin.dashboard': {
    en: 'Dashboard',
    kn: 'Ikicaro'
  },
  'admin.upload': {
    en: 'Upload File',
    kn: 'Ohereza Dosiye'
  },
  'admin.manage': {
    en: 'Manage Files',
    kn: 'Gucunga Dosiye'
  },
  'admin.payments': {
    en: 'Payments',
    kn: 'Kwishyura'
  },
  'admin.users': {
    en: 'Users',
    kn: 'Abakoresha'
  },
  'admin.chat': {
    en: 'Chat',
    kn: 'Ikiganiro'
  },
  'admin.settings': {
    en: 'Settings',
    kn: 'Igenamiterere'
  },
  'admin.stats.files': {
    en: 'Total Files',
    kn: 'Dosiye Zose'
  },
  'admin.stats.users': {
    en: 'Total Users',
    kn: 'Abakoresha Bose'
  },
  'admin.stats.pending': {
    en: 'Pending Payments',
    kn: 'Kwishyura Gutegereje'
  },
  'admin.stats.revenue': {
    en: 'Total Revenue',
    kn: 'Amafaranga Yose'
  },
  'admin.upload.name': {
    en: 'File Name',
    kn: 'Izina rya Dosiye'
  },
  'admin.upload.type': {
    en: 'File Type',
    kn: 'Ubwoko bwa Dosiye'
  },
  'admin.upload.cover': {
    en: 'Cover Image URL',
    kn: "URL y'Ifoto y'Igipfukisho"
  },
  'admin.upload.desc': {
    en: 'Description',
    kn: 'Ibisobanuro'
  },
  'admin.upload.price': {
    en: 'Price (FRW)',
    kn: 'Igiciro (FRW)'
  },
  'admin.upload.btn': {
    en: 'Upload & Host',
    kn: 'Ohereza & Shyira'
  },
  'admin.upload.success': {
    en: 'File uploaded and hosted successfully!',
    kn: 'Dosiye yoherejwe kandi yashyizweho neza!'
  },
  'admin.verify': {
    en: 'Verify',
    kn: 'Emeza'
  },
  'admin.deny': {
    en: 'Deny',
    kn: 'Hakanira'
  },
  'admin.delete': {
    en: 'Delete',
    kn: 'Siba'
  },
  'admin.notifications': {
    en: 'Notifications',
    kn: 'Amakuru'
  },
  // Chat
  'chat.title': {
    en: 'Chat with Admin',
    kn: 'Ganira na Admin'
  },
  'chat.placeholder': {
    en: 'Type a message...',
    kn: 'Andika ubutumwa...'
  },
  'chat.send': {
    en: 'Send',
    kn: 'Ohereza'
  },
  'chat.admin.name': {
    en: 'BAYAVUGE THEOGEN (Admin)',
    kn: 'BAYAVUGE THEOGEN (Admin)'
  },
  'chat.online': {
    en: 'Online',
    kn: 'Ari kumurongo'
  },
  'chat.offline': {
    en: 'Offline',
    kn: 'Ntari kumurongo'
  },
  'chat.attach': {
    en: 'Attach File',
    kn: 'Ongeraho Dosiye'
  },
  'chat.empty': {
    en: 'No messages yet. Start a conversation!',
    kn: 'Nta butumwa burahari. Tangira ikiganiro!'
  },
  'chat.login.required': {
    en: 'Please login to chat with admin',
    kn: 'Nyamuneka injira kugira ngo uganire na admin'
  },
  // Footer
  'footer.developer': {
    en: 'Developer',
    kn: 'Umuhanga mu ikoranabuhanga'
  },
  'footer.admin.contact': {
    en: 'Admin Contact',
    kn: 'Aho Guhamagara Admin'
  },
  'footer.rights': {
    en: 'All rights reserved',
    kn: 'Uburenganzira bwose burabitswe'
  },
  'footer.whatsapp': {
    en: 'WhatsApp',
    kn: 'WhatsApp'
  },
  // Theme
  'theme.light': {
    en: 'Light Mode',
    kn: 'Urumuri'
  },
  'theme.dark': {
    en: 'Dark Mode',
    kn: 'Umwijima'
  },
  // Common
  'common.loading': {
    en: 'Loading...',
    kn: 'Gutegereza...'
  },
  'common.cancel': {
    en: 'Cancel',
    kn: 'Hagarika'
  },
  'common.confirm': {
    en: 'Confirm',
    kn: 'Emeza'
  },
  'common.save': {
    en: 'Save',
    kn: 'Bika'
  },
  'common.back': {
    en: 'Back',
    kn: 'Subira inyuma'
  },
  'common.close': {
    en: 'Close',
    kn: 'Funga'
  },
  'common.yes': {
    en: 'Yes',
    kn: 'Yego'
  },
  'common.no': {
    en: 'No',
    kn: 'Oya'
  }
};
const LanguageContext = createContext<LanguageContextType | undefined>(undefined);
export function LanguageProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [language, setLanguageState] = useState<Language>(() => {
    return localStorage.getItem('edumarket_language') as Language || 'en';
  });
  const [hasSelectedLanguage, setHasSelectedLanguage] = useState(() => {
    return localStorage.getItem('edumarket_language_selected') === 'true';
  });
  const setLanguage = useCallback((lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem('edumarket_language', lang);
  }, []);
  const setHasSelectedLanguageWrapper = useCallback((val: boolean) => {
    setHasSelectedLanguage(val);
    if (val) localStorage.setItem('edumarket_language_selected', 'true');
  }, []);
  const t = useCallback((key: string): string => {
    return translations[key]?.[language] || key;
  }, [language]);
  return <LanguageContext.Provider value={{
    language,
    setLanguage,
    t,
    hasSelectedLanguage,
    setHasSelectedLanguage: setHasSelectedLanguageWrapper
  }} data-id="element-187">
      {children}
    </LanguageContext.Provider>;
}
export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLanguage must be used within LanguageProvider');
  return ctx;
}